import { LoadingContainer } from 'peach/components'
import { useHistory, useParams } from 'peach/router'

import {
  ConfirmDialog,
  Clickable,
  DynamicForm,
  Page,
  PageHeader,
} from 'core/components'
import {
  useDeleteWorkflowStepType,
  useGetWorkitemTypes,
  useGetWorkflowStepTypeById,
  useUpdateWorkflowStepType,
} from 'core/types'
import {
  workflowStepTypeSchema,
  type StaticFieldSchema,
} from 'core/utils/generated/formSchemas'
import { R } from 'core/helpers'

const WorkflowStepType = () => {
  const history = useHistory()
  const { companyId, workflowStepTypeId } = useParams<{
    companyId?: string
    workflowStepTypeId?: string
  }>()

  const {
    data: workflowStepType,
    isLoading,
    refetch,
  } = useGetWorkflowStepTypeById({
    companyId,
    workflowStepTypeId,
  })

  const { mutateAsync: updateWorkflowStepType } = useUpdateWorkflowStepType({
    companyId,
    workflowStepTypeId,
  })

  const { mutateAsync: deleteWorkflowStepType } = useDeleteWorkflowStepType({
    companyId,
    workflowStepTypeId,
  })

  // Build MultiSelect options for workItems from existing work item types
  const { data: workitemTypes } = useGetWorkitemTypes({ companyId })
  const workItemOptions: Array<{
    label: string
    value: string
    sublabel?: string
  }> = Array.isArray(workitemTypes)
    ? (workitemTypes as Array<any>)
        .map((w) => {
          const id = w?.id as string | undefined
          if (!id) return null
          const desc = (w?.descriptionShort ||
            w?.descriptionLong ||
            '') as string
          const truncated = desc
            ? desc.length > 70
              ? desc.slice(0, 70) + '…'
              : desc
            : undefined
          return truncated
            ? { label: id, value: id, sublabel: truncated }
            : { label: id, value: id }
        })
        .filter(
          (v): v is { label: string; value: string; sublabel?: string } =>
            v !== null,
        )
    : []

  const enhancedSchema: Array<StaticFieldSchema> = (
    workflowStepTypeSchema as Array<StaticFieldSchema>
  ).map((f) => (f.key === 'workItems' ? { ...f, options: workItemOptions } : f))

  return (
    <LoadingContainer isLoading={isLoading}>
      <Page variant='full'>
        <PageHeader
          title={workflowStepType?.descriptionShort || 'Workflow Step Type'}
        />
        <DynamicForm
          data={workflowStepType || {}}
          fieldEditMode='toggle'
          fieldOverrides={{ workItems: 'MultiSelect' }}
          onSubmit={async (payload: any) => {
            await updateWorkflowStepType({
              ...payload,
              decisionQuestion: R.isNil(payload.decisionQuestion)
                ? undefined
                : payload.decisionQuestion,
            })
            await refetch()
          }}
          otherActions={[
            <ConfirmDialog
              key='delete-action'
              onConfirm={async () => {
                await deleteWorkflowStepType()
                history.push(`/companies/${companyId}/workflow-step-types`)
              }}
              title='Delete Workflow Step Type'
            >
              <Clickable variant='outline'>Delete</Clickable>
            </ConfirmDialog>,
          ]}
          schema={enhancedSchema}
          validationRules={{
            descriptionShort: [
              (v: unknown) =>
                (typeof v === 'string' && v.trim().length > 0) ||
                'descriptionShort is required',
            ],
            refId: [
              (v: unknown) =>
                (typeof v === 'string' && v.trim().length > 0) ||
                'refId is required',
            ],
          }}
        />
      </Page>
    </LoadingContainer>
  )
}

export default WorkflowStepType
